// Adapter/read-model FRONTEND condiviso per la rappresentazione sintetica di un Extra.
// SOLO normalizzazione di shape: NON ricalcola IVA/totali/business logic (usa i valori
// canonici già forniti dal backend). Usato dal componente canonico ExtraSummaryDialog.

const num = (v) => (typeof v === 'number' ? v : parseFloat(v) || 0);

const STATUS_LABEL = {
  bozza: 'Bozza', richiesto: 'Richiesto', in_preparazione: 'In preparazione', pronto: 'Pronto',
  in_attesa_firma: 'In attesa firma', firmato_cliente: 'Firmato', firmato: 'Firmato',
  in_validazione: 'In validazione', validato: 'Validato', approvato: 'Approvato',
  approvato_admin: 'Approvato', in_attesa_approvazione: 'In attesa approvazione',
  accettato_cliente: 'Accettato', ordinato: 'Ordinato', completato: 'Completato',
  rifiutato: 'Rifiutato',
};
const PRE_SIGN_STATUSES = new Set([
  'bozza', 'richiesto', 'in_preparazione', 'pronto', 'in_attesa_firma',
  'approvato', 'approvato_admin', 'in_attesa_approvazione',
]);

const CAT_LABELS = {
  F: 'Finiture', G: 'Serramenti', H: 'Prodotti', C: 'Lavori interni',
  D: 'Lavori esterni', E: 'Facciate', S: 'Sicurezza',
};

export const extraStatusLabel = (extra) => {
  const s = (extra?.approval_status || extra?.status || '').toLowerCase();
  const base = STATUS_LABEL[s] || extra?.approval_status || extra?.status || '—';
  const signed = !!(extra?.signed_at || extra?.signed_pdf_url);
  if (signed && PRE_SIGN_STATUSES.has(s)) return 'Firmato';
  return base;
};

export function normalizeExtraSummary(extra) {
  if (!extra) return null;
  const net = num(extra.total);
  const gross = num(extra.total_with_vat) || net;
  const vat = Math.max(0, gross - net);
  // Read-model correzioni (SOLO mappatura dal backend, nessuna business logic frontend).
  const cs = extra.corrections_summary || null;
  const state = cs?.economic_correction_state || 'normale';
  const hasCorrections = state === 'stornato' || state === 'parzialmente_stornato';
  const corrections = cs ? {
    state,
    hasCorrections,
    originalNet: num(cs.original_net),
    originalVat: num(cs.original_vat),
    originalGross: num(cs.original_gross),
    correctionsNet: num(cs.corrections_net_effective),
    correctionsVat: num(cs.corrections_vat_effective),
    correctionsGross: num(cs.corrections_gross_effective),
    currentNet: num(cs.current_net),
    currentVat: num(cs.current_vat),
    currentGross: num(cs.current_gross),
    effectiveCount: cs.effective_count || 0,
    totalCount: cs.total_corrections_count || 0,
    hasPending: !!cs.has_pending_correction,
    hasSigned: !!cs.has_signed_correction,
    hasFiscalHold: !!cs.has_fiscal_hold_correction,
    latestStatus: cs.latest_correction_status || null,
    latestId: cs.latest_correction_id || null,
  } : null;
  // Valori da mostrare come PRINCIPALI: correnti se esistono rettifiche effective, altrimenti originari.
  const displayNet = hasCorrections ? corrections.currentNet : net;
  const displayVat = hasCorrections ? corrections.currentVat : vat;
  const displayGross = hasCorrections ? corrections.currentGross : gross;
  return {
    id: extra.id,
    number: extra.extra_number || '—',
    title: extra.title || 'Extra',
    type: extra.extra_type === 'fornitura' ? 'fornitura' : 'lavoro',
    typeLabel: extra.extra_type === 'fornitura' ? 'Extra fornitura' : 'Extra lavori',
    status: extraStatusLabel(extra),
    category: extra.extra_category || null,
    categoryLabel: extra.extra_category ? (CAT_LABELS[extra.extra_category] || null) : null,
    supplier: extra.choice_supplier_name || extra.supplier_name || null,
    itemsCount: (extra.items || []).length,
    net,
    vat,
    gross,
    displayNet,
    displayVat,
    displayGross,
    corrections,
    vatRate: extra.vat_rate ?? null,
    signed: !!(extra.signed_at || extra.signed_pdf_url),
  };
}

// Badge economico correction (read-only). Ritorna {label, cls} o null.
export function correctionBadge(corrections) {
  if (!corrections || !corrections.hasCorrections) return null;
  if (corrections.state === 'stornato') return { label: 'Stornato', cls: 'bg-red-100 text-red-700' };
  return { label: 'Parzialmente stornato', cls: 'bg-orange-100 text-orange-700' };
}

// Indicatore workflow separato (non economico). Ritorna {label, cls} o null.
export function correctionWorkflowIndicator(corrections) {
  if (!corrections) return null;
  if (corrections.hasFiscalHold) return { label: 'Rettifica firmata · da regolarizzare', cls: 'bg-amber-100 text-amber-800' };
  if (corrections.hasPending || corrections.hasSigned) return { label: 'Rettifica in corso', cls: 'bg-slate-100 text-slate-600' };
  return null;
}
